import React, { useMemo } from 'react';
import useFocusTrap from '../../hooks/useFocusTrap';
import './BadgeCelebrationModal.css';

const CONFETTI_COLORS = ['green', 'light-green', 'amber', 'gold', 'white'];
const CONFETTI_COUNT = 40;

const BadgeCelebrationModal = ({ badge, remaining, onDismiss }) => {
  const modalRef = useFocusTrap(!!badge);

  const confettiPieces = useMemo(() => {
    if (!badge) return [];
    return Array.from({ length: CONFETTI_COUNT }, (_, i) => ({
      id: i,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      left: `${Math.random() * 100}%`,
      fallDelay: `${Math.random() * 1.5}s`,
      fallDuration: `${2 + Math.random() * 2}s`,
      spin: `${360 + Math.random() * 720}deg`,
      width: `${6 + Math.random() * 6}px`,
      height: `${6 + Math.random() * 4}px`,
    }));
  }, [badge]);

  React.useEffect(() => {
    if (!badge) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [badge, onDismiss]);

  if (!badge) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onDismiss();
  };

  const buttonText = remaining > 0
    ? `Awesome! (${remaining} more)`
    : 'Awesome!';

  return (
    <div className="badge-celebration-backdrop" onClick={handleBackdropClick}>
      <div
        className="badge-celebration-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="badge-celebration-title"
        ref={modalRef}
      >
        {/* Confetti */}
        <div className="badge-celebration__confetti">
          {confettiPieces.map((piece) => (
            <div
              key={piece.id}
              className={`badge-confetti-piece badge-confetti-piece--${piece.color}`}
              style={{
                left: piece.left,
                width: piece.width,
                height: piece.height,
                '--fall-delay': piece.fallDelay,
                '--fall-duration': piece.fallDuration,
                '--spin': piece.spin,
              }}
            />
          ))}
        </div>

        {/* Close button */}
        <button
          className="badge-celebration__close"
          onClick={onDismiss}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Badge icon */}
        <div className="badge-celebration__icon-wrap">
          <div className="badge-celebration__icon-glow" />
          <span className="badge-celebration__icon" role="img" aria-label={badge.name}>
            {badge.icon || '🏆'}
          </span>
        </div>

        {/* Content */}
        <div className="badge-celebration__label" id="badge-celebration-title">
          Badge Unlocked!
        </div>
        <h2 className="badge-celebration__name">{badge.name}</h2>
        <p className="badge-celebration__description">{badge.description}</p>
        {badge.points > 0 && (
          <div className="badge-celebration__points">
            +{badge.points} points
          </div>
        )}

        {/* Dismiss */}
        <button className="badge-celebration__dismiss" onClick={onDismiss}>
          {buttonText}
        </button>
      </div>
    </div>
  );
};

export default BadgeCelebrationModal;
